import { css } from "@emotion/css";
import { Tooltip, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { DeploymentFreezeListPage, DeploymentFreezeSummary } from "@octopusdeploy/octopus-server-client";
import { DeploymentFreezeStatus } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useParams } from "react-router-dom";
import Chip from "~/components/Chips/Chip";
import InternalLink from "~/components/Navigation/InternalLink/index";
import DateFormatter from "~/utils/DateFormatter/index";
interface DeploymentFreezeDataTableNameCellProps {
    freeze: DeploymentFreezeSummary;
    freezeListPage: DeploymentFreezeListPage;
    hasMissingResource?: boolean;
}
export const DeploymentFreezeDataTableNameCell = ({ freezeListPage, hasMissingResource, freeze }: DeploymentFreezeDataTableNameCellProps) => {
    const routeParams = useParams<{
        spaceId: string | undefined;
        projectSlug: string | undefined;
    }>();
    const { Id: id, Name: name, Start: start, End: end, Status: status, OwnerId: ownerId, SpaceId: spaceId } = freeze;
    const isProjectFreezeListPage = freezeListPage === "project";
    const isProjectLevelFreeze = !isProjectFreezeListPage && ownerId != undefined;
    const isGlobalLevelFreeze = isProjectFreezeListPage && ownerId == undefined;
    const openNewTab = isProjectLevelFreeze || isGlobalLevelFreeze;
    const dateString = () => {
        switch (status) {
            case DeploymentFreezeStatus.Active:
                return `Will end on ${DateFormatter.dateToLongFormat(end)}`;
            case DeploymentFreezeStatus.Scheduled:
                return `Will start on ${DateFormatter.dateToLongFormat(start)}`;
            case DeploymentFreezeStatus.Expired:
                return `Ended on ${DateFormatter.dateToLongFormat(end)}`;
        }
    };
    const getFreezeEditUrl = () => {
        if (ownerId) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return links.projectDeploymentFreezePage.generateUrl({ spaceId: spaceId ?? routeParams.spaceId!, projectSlug: ownerId, deploymentFreezeId: id });
        }
        return links.editDeploymentFreezePage.generateUrl({ deploymentFreezeId: id });
    };
    return (<LegacyDataTableRowColumn className={cellStyles}>
            <div className={nameCellStyles}>
                {isGlobalLevelFreeze && (<Chip noMargin={true} description={!hasMissingResource ? "This freeze has been set at the instance level. Clicking will redirect you to the instance-level editing page." : ""}>
                        Global Freeze
                    </Chip>)}
                {isProjectLevelFreeze && (<Chip noMargin={true} description={!hasMissingResource ? "This freeze has been set at the project level. Clicking will redirect you to the project-level editing page." : ""}>
                        Project Freeze
                    </Chip>)}
                {!hasMissingResource ? (<InternalLink to={getFreezeEditUrl()} openInSelf={!openNewTab}>
                        <div className={nameStyles}>{name}</div>
                    </InternalLink>) : (<div className={nameStyles}>
                        <Tooltip content={"You don't have permission to access one or more projects/tenants/environments connected to this freeze. Please contact your Octopus Administrator to request required permissions or create a new freeze."}>
                            {name}
                        </Tooltip>
                    </div>)}
                <div className={dateStyles}>{dateString()}</div>
            </div>
        </LegacyDataTableRowColumn>);
};
const nameCellStyles = css({
    padding: `${space[16]}, ${space[8]}`,
});
const nameStyles = css({
    padding: `${space[4]} 0`,
});
const dateStyles = css({
    fontSize: "12px",
});
const cellStyles = css({ width: "35%", overflowWrap: "anywhere" });
